import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { GameConfig } from '../config/GameConfig';
import { BaseWeapon, BaseProjectile } from './BaseWeapon';

export class YarnBall extends BaseProjectile {
  private bounces: number = 0;
  public maxBounces: number = GameConfig.WEAPON_DATA.YARN_BALL.MAX_BOUNCES;

  constructor(scene: Phaser.Scene, x: number, y: number, damage: number) {
    super(scene, x, y, '');
    this.createYarnTexture(scene);
    this.setTexture('projectile-yarn');
    this.setBounce(1);
    this.setCollideWorldBounds(true);
  }

  public activate(x: number, y: number, damage: number, speed: number, dirX: number, dirY: number, lifespan: number, angularVelocity: number): void {
    this.setPosition(x, y);
    this.damage = damage;
    this.bounces = 0;

    this.setActive(true);
    this.setVisible(true);

    this.setVelocity(dirX * speed, dirY * speed);
    this.setAngularVelocity(angularVelocity);

    (this.body as Phaser.Physics.Arcade.Body).onWorldBounds = true;
    this.scene.physics.world.on('worldbounds', (body: Phaser.Physics.Arcade.Body) => {
      if (body.gameObject === this) {
        this.hit();
      }
    });

    this.scene.time.delayedCall(lifespan, () => {
      if (this.active) {
        this.setActive(false);
        this.setVisible(false);
        this.setVelocity(0, 0);
      }
    });
  }

  private createYarnTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('projectile-yarn')) {
      return;
    }
    const graphics = scene.add.graphics();
    const size = 16;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillCircle(halfSize, halfSize + 1, 6.5);

    // Main yarn ball body
    graphics.fillStyle(0xFF69B4, 1);
    graphics.fillCircle(halfSize, halfSize, 7);

    // Lighter section for depth
    graphics.fillStyle(0xFFB6D9, 1);
    graphics.fillCircle(halfSize - 1.5, halfSize - 1.5, 5);

    // Yarn strand texture - multiple curved lines
    graphics.lineStyle(1.2, 0xFF1493, 0.9);

    // Horizontal wrapping
    graphics.beginPath();
    graphics.arc(halfSize, halfSize - 2, 5, -Math.PI / 3, Math.PI / 3, false);
    graphics.strokePath();

    graphics.beginPath();
    graphics.arc(halfSize, halfSize, 6, -Math.PI / 4, Math.PI / 4, false);
    graphics.strokePath();

    graphics.beginPath();
    graphics.arc(halfSize, halfSize + 2, 5, -Math.PI / 3, Math.PI / 3, false);
    graphics.strokePath();

    // Vertical wrapping
    graphics.beginPath();
    graphics.arc(halfSize - 2, halfSize, 5, Math.PI / 6, Math.PI * 5 / 6, false);
    graphics.strokePath();

    graphics.beginPath();
    graphics.arc(halfSize + 2, halfSize, 5, Math.PI / 6, Math.PI * 5 / 6, false);
    graphics.strokePath();

    // Loose strand hanging off
    graphics.lineStyle(1, 0xFF1493, 1);
    graphics.beginPath();
    graphics.moveTo(halfSize + 5, halfSize - 3);
    graphics.lineTo(halfSize + 7, halfSize - 1);
    graphics.lineTo(halfSize + 8, halfSize + 1);
    graphics.strokePath();

    // Highlight shine
    graphics.fillStyle(0xFFFFFF, 0.6);
    graphics.fillCircle(halfSize - 2, halfSize - 2, 1.5);

    graphics.generateTexture('projectile-yarn', size, size);
    graphics.destroy();
  }

  public hit(): void {
    this.bounces++;
    if (this.bounces >= this.maxBounces) {
      this.setActive(false);
      this.setVisible(false);
      this.setVelocity(0, 0);
    }
  }
}

export class YarnBallWeapon extends BaseWeapon {
  private directionCount: number = 4;
  private baseBounces: number = GameConfig.WEAPON_DATA.YARN_BALL.MAX_BOUNCES;
  private attackDelay: number = GameConfig.WEAPON_DATA.YARN_BALL.COOLDOWN;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    super(scene, player, enemies, YarnBall);
    this.startAutoAttack();

    this.projectiles.createMultiple({
      classType: YarnBall,
      key: 'projectile-yarn',
      repeat: 20 - 1,
      active: false,
      visible: false,
    });
  }

  protected startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: this.attackDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  protected attack(): void {
    // Fire in multiple directions based on directionCount
    const angleStep = (Math.PI * 2) / this.directionCount;
    for (let i = 0; i < this.directionCount; i++) {
      const angle = i * angleStep;
      const dirX = Math.cos(angle);
      const dirY = Math.sin(angle);
      this.fireYarnBall(dirX, dirY);
    }
  }

  private fireYarnBall(dirX: number, dirY: number): void {
    const yarnBall = this.projectiles.get(this.player.x, this.player.y) as YarnBall;
    if (yarnBall) {
      yarnBall.maxBounces = this.baseBounces;
      yarnBall.activate(
        this.player.x,
        this.player.y,
        GameConfig.WEAPON_DATA.YARN_BALL.DAMAGE * this.damageMultiplier,
        GameConfig.WEAPON_DATA.YARN_BALL.PROJECTILE_SPEED,
        dirX,
        dirY,
        GameConfig.WEAPON_DATA.YARN_BALL.LIFESPAN,
        GameConfig.WEAPON_DATA.YARN_BALL.ROTATION_SPEED
      );
    }
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public addDirections(): void {
    this.directionCount += 2;
  }

  public addBounces(amount: number): void {
    this.baseBounces += amount;
  }
}
